<script>
import { Overlay } from '../../'
import { EventBus } from '../../EventBus'

export default {
    name: 'MTFTargets',
    mixins: [Overlay],
    data() {
        return {
            timeframeData: {}
        }
    },
    computed: {
        sett() {
            return this.$props.settings || {}
        },
        upBarColor() {
            return this.sett.upBarColor || 'rgb(38, 166, 154)'
        },
        downBarColor() {
            return this.sett.downBarColor || 'rgb(239, 83, 80)'
        },
        pendingTargetColor() {
            return this.sett.pendingTargetColor || 'rgb(92, 92, 92)'
        },
        label_font() {
            return this.sett.font || ('10px ' + (this.$props.font || '').split('px').pop())
        },
        show_labels() {
            return this.sett.showLabels !== false
        },
        show_entry() {
            return this.sett.showEntry === true
        },
        show_stoploss() {
            return this.sett.showStoploss === true
        },
        is_dark() {
            const back = this.$props.colors ? this.$props.colors.back : ''
            return back && back !== '#fff' && back !== '#ffffff' && back !== 'white'
        }
    },
    methods: {
        meta_info() {
            return {
                author: 'TradingCandle',
                version: '2.3.0',
                desc: 'Multi-Timeframe Targets - Shows latest VT and Laser target levels with dotted lines and price markers',
                preset: {
                    name: 'MTF Targets',
                    side: 'onchart',
                    settings: {
                        showLabels: true,
                        showEntry: false,
                        showStoploss: false,
                        'z-index': 10
                    }
                }
            }
        },
        init() {
            this.shaderRegistered = false
            EventBus.$on('timeframes-updated', this.onTimeframesUpdated)
            this.$nextTick(() => {
                EventBus.$emit('request-timeframes')
            })
        },
        destroy() {
            EventBus.$off('timeframes-updated', this.onTimeframesUpdated)
        },
        onTimeframesUpdated(data) {
            if (data && typeof data === 'object') {
                this.timeframeData = data
            }
        },
        draw(ctx) {
            const layout = this.$props.layout
            if (!layout || !layout.width) return

            const targets = this.collectTargets()
            if (!targets || targets.length === 0) return

            if (!this.shaderRegistered) {
                this.initShader()
            }

            ctx.save()

            // Get X position from last candle (dotted lines start here)
            let startX = 0
            if (layout.full_candles && layout.full_candles.length > 0) {
                startX = layout.full_candles[layout.full_candles.length - 1].x
            }

            for (const t of targets) {
                // VT Target — dotted line + label
                if (t.vtTarget && t.vtTarget > 0) {
                    const y = Math.round(layout.$2screen(t.vtTarget))
                    if (y > 0 && y < layout.height) {
                        const color = t.vtDirection === 'BUY' ? this.upBarColor : this.downBarColor
                        this.drawDottedLine(ctx, startX, y, layout.width, color)
                        if (this.show_labels) {
                            this.drawPriceLabel(ctx, layout, y, color, t.tf, t.vtTarget)
                        }
                    }
                }

                // Entry (optional, off by default)
                if (this.show_entry && t.entry && t.entry > 0) {
                    const y = Math.round(layout.$2screen(t.entry))
                    if (y > 0 && y < layout.height) {
                        const color = t.vtDirection === 'BUY' ? this.upBarColor : this.downBarColor
                        this.drawDottedLine(ctx, startX, y, layout.width, color)
                        if (this.show_labels) {
                            this.drawPriceLabel(ctx, layout, y, color, t.tf + ' E', t.entry)
                        }
                    }
                }

                // Stoploss (optional, off by default)
                if (this.show_stoploss && t.stoploss && t.stoploss > 0) {
                    const y = Math.round(layout.$2screen(t.stoploss))
                    if (y > 0 && y < layout.height) {
                        this.drawDottedLine(ctx, startX, y, layout.width, this.downBarColor)
                        if (this.show_labels) {
                            this.drawPriceLabel(ctx, layout, y, this.downBarColor, t.tf + ' S', t.stoploss)
                        }
                    }
                }
            }

            ctx.restore()
        },
        drawDottedLine(ctx, x1, y, x2, color) {
            ctx.strokeStyle = color
            ctx.lineWidth = 1
            ctx.setLineDash([2, 2])
            ctx.beginPath()
            ctx.moveTo(x1, y)
            ctx.lineTo(x2, y)
            ctx.stroke()
            ctx.setLineDash([])
        },
        initShader() {
            let comp = this
            let layout = this.$props.layout
            let config = this.$props.config

            this.$emit('new-shader', {
                target: 'sidebar', draw: ctx => {
                    const targets = comp.collectTargets()
                    if (!targets || targets.length === 0) return
                    if (!layout || !config) return

                    const h = config.PANHEIGHT || 22
                    const w = ctx.canvas.width
                    const prec = layout.prec || 2

                    for (const t of targets) {
                        // VT Target in sidebar
                        if (t.vtTarget && t.vtTarget > 0) {
                            const y = layout.$2screen(t.vtTarget)
                            if (y > 0 && y < layout.height) {
                                const color = t.vtDirection === 'BUY' ? comp.upBarColor : comp.downBarColor
                                ctx.fillStyle = color
                                ctx.globalAlpha = 0.8
                                ctx.fillRect(-0.5, y - h * 0.5, w + 1, h)
                                ctx.globalAlpha = 1
                                ctx.fillStyle = '#fff'
                                ctx.textAlign = 'left'
                                ctx.fillText(`${t.tf} ${t.vtTarget.toFixed(prec)}`, 4, y + 4)
                            }
                        }

                        // Entry in sidebar (optional)
                        if (comp.show_entry && t.entry && t.entry > 0) {
                            const y = layout.$2screen(t.entry)
                            if (y > 0 && y < layout.height) {
                                const color = t.vtDirection === 'BUY' ? comp.upBarColor : comp.downBarColor
                                ctx.fillStyle = color
                                ctx.globalAlpha = 0.6
                                ctx.fillRect(-0.5, y - h * 0.5, w + 1, h)
                                ctx.globalAlpha = 1
                                ctx.fillStyle = '#fff'
                                ctx.textAlign = 'left'
                                ctx.fillText(`${t.tf}E ${t.entry.toFixed(prec)}`, 4, y + 4)
                            }
                        }

                        // Stoploss in sidebar (optional)
                        if (comp.show_stoploss && t.stoploss && t.stoploss > 0) {
                            const y = layout.$2screen(t.stoploss)
                            if (y > 0 && y < layout.height) {
                                ctx.fillStyle = comp.downBarColor
                                ctx.globalAlpha = 0.6
                                ctx.fillRect(-0.5, y - h * 0.5, w + 1, h)
                                ctx.globalAlpha = 1
                                ctx.fillStyle = '#fff'
                                ctx.textAlign = 'left'
                                ctx.fillText(`${t.tf}S ${t.stoploss.toFixed(prec)}`, 4, y + 4)
                            }
                        }
                    }
                }
            })
            this.shaderRegistered = true
        },
        drawPriceLabel(ctx, layout, y, color, tfLabel, price) {
            const label = `${tfLabel} ${this.formatNum(price)}`
            ctx.font = this.label_font
            const textWidth = ctx.measureText(label).width
            const labelX = layout.width - textWidth - 10
            const labelH = 13
            const labelW = textWidth + 8

            ctx.fillStyle = color
            ctx.globalAlpha = 0.85
            ctx.fillRect(labelX - 4, y - labelH / 2, labelW, labelH)
            ctx.globalAlpha = 1

            ctx.fillStyle = '#ffffff'
            ctx.textAlign = 'left'
            ctx.textBaseline = 'middle'
            ctx.fillText(label, labelX, y)
        },
        collectTargets() {
            const targets = []
            const tfData = this.sett.timeframeData || this.timeframeData || {}

            const tfOrder = ['1m', '3m', '4m', '5m', '10m', '15m', '30m', '40m', '1H', '2H', '4H', '400m', '1D']
            const sortedKeys = Object.keys(tfData).sort((a, b) => {
                const aIdx = tfOrder.indexOf(a)
                const bIdx = tfOrder.indexOf(b)
                return (aIdx >= 0 ? aIdx : 99) - (bIdx >= 0 ? bIdx : 99)
            })

            for (const tf of sortedKeys) {
                const item = tfData[tf]
                if (!item || !item.entry) continue
                const entry = item.entry
                const className = String(item.className || '')
                if (className.includes('p-hidden')) continue

                // Show all timeframes that have valid targets (including completed)
                // Removed the isCompleted check to show all timeframe targets

                let vtTarget = 0
                let vtDirection = ''
                let laserTarget = 0
                let laserDirection = ''
                let entryPrice = 0
                let stoploss = 0

                // VT Target
                if (entry.call && entry.target) {
                    vtTarget = Number(entry.target) || 0
                    vtDirection = entry.call
                    entryPrice = Number(entry.entry) || 0
                    stoploss = Number(entry.stoploss) || 0
                }

                // VT Laser (flash) Target
                if (entry.flashSide && entry.flashTarget) {
                    laserTarget = Number(entry.flashTarget) || 0
                    laserDirection = entry.flashSide
                }

                // Include targets for all timeframes that have any target data
                if (vtTarget > 0 || laserTarget > 0 || (entry.entry && entry.entry > 0)) {
                    targets.push({
                        tf: item.label || tf,
                        vtTarget,
                        vtDirection,
                        laserTarget,
                        laserDirection,
                        entry: entryPrice,
                        stoploss
                    })
                }
            }
            return targets
        },
        formatNum(num) {
            if (!num || isNaN(num)) return '-'
            const n = Number(num)
            if (n >= 10000) return n.toFixed(0)
            if (n >= 100) return n.toFixed(1)
            return n.toFixed(2)
        },
        use_for() { return ['MTFTargets'] },
        legend() { return [] }
    }
}
</script>
